// Web port of app/src/contexts/FlagContext.js — keep in sync with it.
//
// The app keeps a small set of user flags — 'pilot' and 'debug' — that gate
// features still being trialled or only useful to developers. Which flags a user
// MAY switch on comes from their Cognito groups (the auth context's eligableGroups:
// an account in the `debug` group may use the debug flag); which of those they
// HAVE switched on is a stored preference (flagStorage.js). What the rest of the
// app sees, activeUserFlags, is the intersection: a stored flag the account is not
// eligible for is inert, and a stored preference survives sign-out / sign-in
// without ever granting more than the token allows.
//
// Two deliberate differences from the mobile provider:
//   • the store is read synchronously on first render (localStorage is sync), so
//     there is no flags-off flash while an async read completes;
//   • the app's autoFlags ('geolocation_logging', switched on by itself for
//     eligible users) are omitted — background geolocation is a mobile-only
//     concern with no web counterpart.
//
// The context carries a usable default (no flags, a no-op toggle) rather than
// null, as the app's does, so a screen that consults a flag renders "flag off" in
// a tree with no provider (e.g. a unit test) instead of throwing.
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useAuth } from '../auth/AuthenticationContext.jsx';
import { getStoredFlags, setStoredFlags } from './flagStorage.js';

// The flags a user can switch on and off themselves (the app's toggleableFlags),
// in the order the menu lists them.
export const TOGGLEABLE_FLAGS = ['pilot', 'debug'];

const NO_FLAGS = [];
const noop = () => {};

export const FlagContext = createContext({
  activeUserFlags: NO_FLAGS,
  toggleableFlags: TOGGLEABLE_FLAGS,
  handleFlagToggle: noop,
});

export function FlagProvider({ children }) {
  // Defaulted so the provider is safe under a test double of useAuth that omits
  // the groups; the real context always supplies an array.
  const { eligableGroups = NO_FLAGS } = useAuth();
  const [storedFlags, setFlags] = useState(getStoredFlags);

  const handleFlagToggle = useCallback(
    (flag) => {
      const next = storedFlags.includes(flag)
        ? storedFlags.filter((stored) => stored !== flag)
        : [...storedFlags, flag];
      setFlags(next);
      setStoredFlags(next);
    },
    [storedFlags],
  );

  const activeUserFlags = useMemo(
    () => storedFlags.filter((flag) => eligableGroups.includes(flag)),
    [storedFlags, eligableGroups],
  );

  const value = useMemo(
    () => ({
      activeUserFlags,
      toggleableFlags: TOGGLEABLE_FLAGS,
      handleFlagToggle,
    }),
    [activeUserFlags, handleFlagToggle],
  );

  return <FlagContext.Provider value={value}>{children}</FlagContext.Provider>;
}

// Is `flag` active for this user? The app's useFlag: the one call feature code
// makes to branch on a flag.
export function useFlag(flag) {
  const { activeUserFlags = NO_FLAGS } = useContext(FlagContext);
  return activeUserFlags.includes(flag);
}
